import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { TooltipButton } from "@/components/ui/TooltipButton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Receipt } from "lucide-react";
import { toast } from "sonner";

export default function PurchaseHistory() {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadPurchaseHistory();
  }, []);

  const loadPurchaseHistory = async () => {
    try {
      const { data: { session } } = await digitalOcean.auth.getSession();
      if (!session) {
        navigate("/Auth");
        return;
      }

      const { data, error } = await digitalOcean
        .from("credit_transactions")
        .select("*")
        .eq("user_id", session.user.id)
        .eq("transaction_type", "purchase")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPurchases(data || []);
    } catch (error) {
      toast.error("Failed to load purchase history: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <TooltipButton
              onClick={() => navigate(-1)}
              variant="outline"
              size="icon"
              className="neuro-base rounded-xl"
              tooltip="Go back"
            >
              <ArrowLeft className="w-5 h-5" />
            </TooltipButton>
            <h1 className="text-4xl font-bold text-foreground flex items-center gap-3">
              <Receipt className="w-10 h-10" />
              Purchase History
            </h1>
          </div>
        </div>

        {purchases.length === 0 ? (
          <div className="bg-card p-12 rounded-lg border border-border text-center">
            <Receipt className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold text-card-foreground mb-2">No Purchases Yet</h2>
            <p className="text-muted-foreground mb-6">
              Your credit purchase history will appear here once you make your first purchase.
            </p>
            <TooltipButton
              onClick={() => navigate("/buy-credits")}
              className="neuro-base rounded-2xl"
              tooltip="Go to the buy credits page"
            >
              Buy Credits
            </TooltipButton>
          </div>
        ) : (
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount (USD)</TableHead>
                  <TableHead>Credits Added</TableHead>
                  <TableHead>Packages</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchases.map((purchase) => {
                 const amountUsdRaw = purchase.metadata?.amount_usd;
                 const amountUsd = amountUsdRaw
                   ? Number(amountUsdRaw).toFixed(2)
                   : purchase.amount
                   ? (purchase.amount / 100).toFixed(2)
                   : "0.00";

                 const creditsAddedRaw =
                   purchase.metadata?.credits_added !== undefined
                     ? purchase.metadata.credits_added
                     : purchase.amount;
                 const creditsAdded = creditsAddedRaw
                   ? (creditsAddedRaw / 100).toFixed(2)
                   : "0.00";

                 const quantity = purchase.metadata?.quantity || 1;
                 const packageLabel = quantity === 1 ? "package" : "packages";

                 return (
                   <TableRow key={purchase.id}>
                     <TableCell className="font-medium">
                       {formatDate(purchase.created_at)}
                     </TableCell>
                     <TableCell className="text-[#b0a090]">
                       ${amountUsd}
                     </TableCell>
                     <TableCell>
                       ${creditsAdded}
                     </TableCell>
                     <TableCell>
                       {quantity} {packageLabel}
                     </TableCell>
                   </TableRow>
                 );
               })}
              </TableBody>
            </Table>
          </div>
        )}

        {purchases.length > 0 && (
          <div className="mt-4 text-sm text-muted-foreground text-center">
            Total Purchases: {purchases.length}
          </div>
        )}
      </div>
    </div>
  );
}
